import { useCallback, useMemo } from 'react';

import { OptionType } from '@evinced-private/ui-common';

import { markOptionsWithWarnings } from 'src/common/helpers/ConfigurationWarningsHelper';
import { DEFAULT_PERIOD_OPTION } from 'src/common/helpers/DataFilterHelper';
import { WEB_SDK_TEST_TABLE_ID } from 'src/common/pages/dashboards-container/dashboard_consts';
import { useTableState } from 'src/common/providers/dashboard/TableStateProvider';

import { TESTS_PERIOD_OPTIONS } from '../../../helpers/WebSdkConsts';
import { extractValueFromLabel, transformLabelsToV2Format } from '../helpers/filter-section-v2';
import {
	LABEL_PREFIXES,
	UseFilterSectionV2Props,
	V2FilterOptions,
	V2Filters
} from '../types/filter-section-v2';

import { useConfiguredValues } from './useConfiguredValues';

export const useFilterSectionV2 = ({
	testLabelsData,
	propertiesOptions,
	filters
}: UseFilterSectionV2Props): {
	v2FilterOptions: V2FilterOptions;
	v2Filters: V2Filters;
	handleV2FilterChange: (newFilters: V2Filters) => void;
} => {
	const { onFilterChange } = useTableState();
	const configuredValues = useConfiguredValues();

	// Transform flat labels array to V2 structured format
	const transformedLabels = useMemo(() => {
		if (!testLabelsData) {
			return {
				environmentFilters: [],
				unitIdFilters: [],
				customLabelFilters: [],
				allCustomLabelFilters: []
			};
		}
		return transformLabelsToV2Format(testLabelsData);
	}, [testLabelsData]);

	// Use the structured filter options from transformed labels, with warnings for mismatched values
	const v2FilterOptions = useMemo<V2FilterOptions>(() => {
		return {
			environment: markOptionsWithWarnings(
				transformedLabels.environmentFilters,
				configuredValues.environment,
				configuredValues.environmentDisplayName,
				extractValueFromLabel
			),
			unitId: markOptionsWithWarnings(
				transformedLabels.unitIdFilters,
				configuredValues.unitId,
				configuredValues.unitIdDisplayName,
				extractValueFromLabel
			),
			labels: transformedLabels.allCustomLabelFilters,
			properties: propertiesOptions ?? [],
			testPeriod: TESTS_PERIOD_OPTIONS
		};
	}, [transformedLabels, propertiesOptions, configuredValues]);

	// Map current filters to V2 format
	const v2Filters = useMemo<V2Filters>(() => {
		const allLabels = filters?.labels ?? [];
		const environmentFilters: OptionType[] = [];
		const unitIdFilters: OptionType[] = [];
		const customLabelFilters: OptionType[] = [];

		allLabels.forEach((label) => {
			const labelValue = String(label.value);
			if (labelValue.startsWith(LABEL_PREFIXES.ENVIRONMENT)) {
				environmentFilters.push(label);
			} else if (labelValue.toLowerCase().startsWith(LABEL_PREFIXES.UNIT_ID)) {
				unitIdFilters.push(label);
			} else {
				customLabelFilters.push(label);
			}
		});

		return {
			environment: environmentFilters.length > 0 ? environmentFilters : undefined,
			unitId: unitIdFilters.length > 0 ? unitIdFilters : undefined,
			labels: customLabelFilters.length > 0 ? customLabelFilters : undefined,
			properties: filters?.properties,
			testPeriod: filters?.testPeriod ?? [DEFAULT_PERIOD_OPTION],
			searchValue: filters?.searchValue
		};
	}, [filters]);

	// Handler for FilterSectionV2 filter changes
	const handleV2FilterChange = useCallback(
		(newFilters: V2Filters) => {
			// Combine all label filters back into a single array for compatibility with IFilterOptions
			const combinedLabels: OptionType[] = [
				...(newFilters.environment ?? []),
				...(newFilters.unitId ?? []),
				...(newFilters.labels ?? [])
			];

			const updatedFilters = {
				...filters,
				unitId: newFilters.unitId,
				labels: combinedLabels.length > 0 ? combinedLabels : undefined,
				properties: newFilters.properties,
				testPeriod: newFilters.testPeriod,
				searchValue: newFilters.searchValue
			};
			onFilterChange(WEB_SDK_TEST_TABLE_ID, updatedFilters);
		},
		[filters, onFilterChange]
	);

	return {
		v2FilterOptions,
		v2Filters,
		handleV2FilterChange
	};
};
